import { User } from '../types';
import { apiClient } from './apiClient';

interface LoginResponse {
  user: User;
  token: string;
  permissions?: string[];
  isSuperAdmin?: boolean;
}

/**
 * Iniciar sesión en la aplicación
 */
export const login = async (email: string, password: string): Promise<LoginResponse> => {
  try {
    const response = await apiClient.post<LoginResponse>('/auth/login', { email, password });
    // Guardar token en localStorage
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
    }
    return response.data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Error desconocido al iniciar sesión');
  }
};

/**
 * Registrar un nuevo usuario
 */
export const register = async (
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  jobTitle?: string
): Promise<LoginResponse> => {
  try {
    const response = await apiClient.post<LoginResponse>('/auth/register', {
      firstName,
      lastName,
      email,
      password,
      jobTitle
    });
    // Guardar token en localStorage
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
    }
    return response.data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Error desconocido al registrar usuario');
  }
};

/**
 * Cerrar sesión
 */
export const logout = async (): Promise<void> => {
  try {
    // Eliminar token de localStorage
    localStorage.removeItem('auth_token');
  } catch (error) {
    console.error('Error al cerrar sesión:', error);
  }
};

interface UserResponse {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  email: string;
  role?: {
    id: number;
    name: string;
    isSuperAdmin?: boolean;
    defaultDashboard?: string | null;
  };
  permissions?: string[];
  isSuperAdmin?: boolean;
}

/**
 * Obtener información del usuario actual y sus permisos
 */
export const getCurrentUser = async (): Promise<UserResponse> => {
  try {
    const response = await apiClient.get<UserResponse>('/auth/me');
    return response.data;
  } catch (error) {
    throw new Error('Error al obtener información del usuario');
  }
};

/**
 * Verificar si el token actual es válido
 */
export const validateToken = async (): Promise<UserResponse | null> => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;

    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    const userData = await getCurrentUser();
    return userData;
  } catch (error) {
    console.error('Token inválido:', error);
    logout();
    return null;
  }
};

export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  try {
    const response = await apiClient.post<{ success: boolean; message: string }>('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Error al cambiar la contraseña');
    }
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const ax = err as { response?: { data?: { message?: string } } };
      if (ax.response?.data?.message) {
        throw new Error(ax.response.data.message);
      }
    }
    throw err;
  }
};

export const forgotPassword = async (email: string): Promise<{ resetToken?: string }> => {
  const response = await apiClient.post<{ message: string; resetToken?: string }>(
    '/auth/forgot-password',
    { email }
  );
  return { resetToken: response.data.resetToken };
};

export const resetPassword = async (
  token: string,
  newPassword: string
): Promise<void> => {
  try {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/auth/reset-password',
      { token, newPassword }
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Error al restablecer la contraseña');
    }
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const ax = err as { response?: { data?: { message?: string } } };
      if (ax.response?.data?.message) {
        throw new Error(ax.response.data.message);
      }
    }
    throw err;
  }
}; 